import React, { useState } from "react";
import axios from "axios";
import { monthNames } from "../../util/rekapAbsensiUtils";
import moment from "moment";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import DeleteIcon from "../../asset/delete_icon.png";
import { hasRole } from "../../util/function";
import "./DetailAbsensiTable.css";

const DAY_FMT = "ddd MMM D YYYY";

const toLocalInput = (iso) =>
  iso ? moment.parseZone(iso).utcOffset(7).format("YYYY-MM-DDTHH:mm:ss") : "";

const splitDayTime = (localInput) => {
  const m = moment(localInput, ["YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DDTHH:mm"]);
  return { day: m.format(DAY_FMT), time: m.format("HH:mm:ss") };
};

const formatTanggalSingkat = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")} ${monthNames[
    d.getMonth()
  ].slice(0, 3)} ${d.getFullYear()}`;
};

const formatJamSingkat = (value) => {
  if (!value) return "-";
  if (typeof value === "string" && value.includes("T")) {
    return value.split("T")[1]?.slice(0, 8) || "-";
  }
  const parts = String(value).trim().split(" ");
  return parts[parts.length - 1] || "-";
};

const formatTanggalMoment = (value) => {
  if (!value) return "-";
  const m = moment(value);
  if (!m.isValid()) return "-";
  return m.utcOffset(7).format("DD-MM-YY");
};

const renderClockIn = (item) => {
  const absensiJam = formatJamSingkat(item.clockIn);
  const ambilJam = item.requestAmbilJamMasuk || null;

  if (item.clockIn && ambilJam) {
    return (
      <span>
        {absensiJam}
        <span className="ambil-inline-note">(+Ambil {ambilJam})</span>
      </span>
    );
  }

  if (!item.clockIn && ambilJam) {
    return <span className="ambil-only-text">{ambilJam}</span>;
  }

  return absensiJam;
};

const renderClockOut = (item) => {
  const absensiJam = formatJamSingkat(item.clockOut);
  const ambilJam = item.requestAmbilJamKeluar || null;

  if (item.clockOut && ambilJam) {
    return (
      <span>
        {absensiJam}
        <span className="ambil-inline-note">(+Ambil {ambilJam})</span>
      </span>
    );
  }

  if (!item.clockOut && ambilJam) {
    return <span className="ambil-only-text">{ambilJam}</span>;
  }

  return absensiJam;
};

const KETERANGAN_CONFIG = [
  {
    key: "sakit",
    label: "Sakit",
    color: "#be123c",
    bg: "#ffe4e6",
    test: (item) => item.isSakit === true || item.isSakitDitolakType === true,
  },
  {
    key: "izin",
    label: "Izin",
    color: "#7e22ce",
    bg: "#f3e8ff",
    test: (item) => item.isIzin === true || item.isIzinDitolakType === true,
  },
  {
    key: "izinDitolak",
    label: "Izin Ditolak",
    color: "#b91c1c",
    bg: "#fee2e2",
    test: (item) => item.isIzinDitolak === true,
  },
  {
    key: "alpha",
    label: "Alpha",
    color: "#991b1b",
    bg: "#fee2e2",
    test: (item) => item.isAlpha === true,
  },
  {
    key: "ambil",
    label: "Ambil",
    color: "#b45309",
    bg: "#fef3c7",
    test: (item) => item.isAmbil === true,
  },
  {
    key: "lembur",
    label: "Lembur",
    color: "#1d4ed8",
    bg: "#dbeafe",
    test: (item) => item.hasLembur === true,
  },
  {
    key: "kerja",
    label: "Kerja",
    color: "#15803d",
    bg: "#dcfce7",
    test: (item) => item.isKerja === true,
  },
  {
    key: "probation",
    label: "Probation",
    color: "#c2650f",
    bg: "#ffedd5",
    test: (item) => item.isProbation === true,
  },
  {
    key: "jadwalLibur",
    label: "Jadwal Libur",
    color: "#0f766e",
    bg: "#ccfbf1",
    test: (item) => item.absensiType === "jadwal libur",
  },
  {
    key: "liburMinggu",
    label: "Libur Minggu",
    color: "#78350f",
    bg: "#fef0e6",
    test: (item) => item.absensiType === "libur minggu",
  },
  {
    key: "setengahHari",
    label: "Setengah Hari",
    color: "#ffffff",
    bg: "#ec4899",
    test: (item) => item.isSetengahHari === true,
  },
];

const KeteranganBadges = ({ item }) => {
  const matches = KETERANGAN_CONFIG.filter((c) => c.test(item));

  if (matches.length === 0) {
    return (
      <span className="keterangan-badge keterangan-badge-default">
        Absen Biasa
      </span>
    );
  }

  return (
    <div className="keterangan-badge-group">
      {matches.map((m) => (
        <span
          key={m.key}
          className="keterangan-badge"
          style={{
            color: m.color,
            background: m.bg,
            border: `1px solid ${m.color}33`,
          }}
        >
          {m.label}
        </span>
      ))}
    </div>
  );
};

const DetailAbsensiTable = ({ detail, userId, onChanged }) => {
  const list = Array.isArray(detail) ? detail : [];

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const token = localStorage.getItem("auth");
  const canEdit = !!userId && hasRole(user, "hrd_admin", "super_user");

  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({
    clockIn: "",
    clockOut: "",
    probation: false,
  });
  const [deleteIdx, setDeleteIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  const post = (body) =>
    axios.post(`${process.env.REACT_APP_ENDPOINT}/updateUser`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });

  const startEdit = (i, item) => {
    setEditIdx(i);
    setForm({
      clockIn: toLocalInput(item.clockIn),
      clockOut: toLocalInput(item.clockOut || item.clockIn),
      probation: item.isProbation === true,
    });
  };

  const saveEdit = async (item) => {
    if (!form.clockIn || !form.clockOut) return;
    setBusy(true);
    try {
      const inP = splitDayTime(form.clockIn);
      const outP = splitDayTime(form.clockOut);
      await post({
        id: userId,
        type: "absent",
        dataEdit: moment.parseZone(item.clockIn).utcOffset(7).format(DAY_FMT),
        clockInOriginal: item.clockIn,
        probation: form.probation, // hanya masuk ke collection 'absensi'
        absent: {
          clockIn: inP,
          clockOut: outP,
        },
      });
      setEditIdx(null);
      onChanged && (await onChanged());
    } catch (e) {
      console.log(e);
      alert("Gagal menyimpan perubahan");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    const item = list[deleteIdx];
    setBusy(true);
    try {
      const m = moment.parseZone(item.clockIn).utcOffset(7);
      await post({
        id: userId,
        type: "deleteAbsent",
        day: m.format(DAY_FMT),
        time: m.format("HH:mm:ss"),
      });
      setDeleteIdx(null);
      onChanged && (await onChanged());
    } catch (e) {
      console.log(e);
      alert("Gagal menghapus data");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="detail-absensi-wrapper">
      <div className="detail-absensi-header">
        Detail Absensi ({list.length} Data)
      </div>

      <div className="detail-absensi-scroll">
        <table className="detail-absensi-table">
          <thead>
            <tr className="detail-absensi-thead-row">
              <th className="detail-th">No.</th>
              <th className="detail-th">Tanggal</th>
              <th className="detail-th">Tgl In</th>
              <th className="detail-th">Clock In</th>
              <th className="detail-th">Tgl Out</th>
              <th className="detail-th">Clock Out</th>
              <th className="detail-th">Keterangan</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="detail-absensi-empty">
                  Tidak ada data absensi
                </td>
              </tr>
            ) : (
              list.map((item, i) => {
                const isEditing = editIdx === i;
                const hasRequestFlag =
                  item.isAlpha === true ||
                  item.isIzin === true ||
                  item.isSakit === true ||
                  item.isKerja === true ||
                  item.isAmbil === true ||
                  item.isSetengahHari === true;

                const editable = canEdit && !!item.clockIn && !hasRequestFlag;
                return (
                  <React.Fragment key={i}>
                    <tr className="detail-absensi-row">
                      <td className="detail-td">{i + 1}.</td>
                      <td className="detail-td">
                        {formatTanggalSingkat(item.tanggal)}
                      </td>
                      <td className="detail-td">
                        {formatTanggalMoment(item.clockIn)}
                      </td>
                      <td className="detail-td">{renderClockIn(item)}</td>
                      <td className="detail-td">
                        {formatTanggalMoment(item.clockOut)}
                      </td>
                      <td className="detail-td">{renderClockOut(item)}</td>
                      <td className="detail-td detail-td-keterangan">
                        <KeteranganBadges item={item} />
                      </td>
                    </tr>

                    {isEditing && (
                      <tr className="detail-absensi-edit-row">
                        <td colSpan={8} className="detail-td">
                          <div className="detail-edit-form">
                            <label>
                              Clock In
                              <input
                                type="datetime-local"
                                step="1"
                                value={form.clockIn}
                                onChange={(e) =>
                                  setForm({ ...form, clockIn: e.target.value })
                                }
                              />
                            </label>
                            <label>
                              Clock Out
                              <input
                                type="datetime-local"
                                step="1"
                                value={form.clockOut}
                                onChange={(e) =>
                                  setForm({ ...form, clockOut: e.target.value })
                                }
                              />
                            </label>
                            <label className="detail-edit-check">
                              <input
                                type="checkbox"
                                checked={form.probation}
                                onChange={(e) =>
                                  setForm({
                                    ...form,
                                    probation: e.target.checked,
                                  })
                                }
                              />
                              Probation
                            </label>
                            <button
                              type="button"
                              className="detail-btn detail-btn-save"
                              disabled={busy}
                              onClick={() => saveEdit(item)}
                            >
                              {busy ? "Menyimpan..." : "Simpan"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={deleteIdx !== null}
        title="Hapus data absen?"
        loading={busy}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteIdx(null)}
      >
        Data absen ini akan dihapus permanen dan tidak bisa dikembalikan.
      </ConfirmModal>
    </div>
  );
};

export default DetailAbsensiTable;
